/**
 * Loader - the app's single branded loading mark (the walking brand
 * figure). Use it for every indeterminate wait:
 *
 * - Route transitions (router Suspense fallback) -> `fullScreen`.
 * - Page / panel data loads where there is no content to skeleton ->
 *   `fullScreen`.
 * - Busy buttons and inline waits -> `size="sm"` (the Button renders this
 *   for you when `isLoading`).
 *
 * LOADER STANDARD (Lior, 2026-06-26 · "Width-fit in cards"): ONE gif, the
 * SAME 16:9 aspect ratio EVERYWHERE. Small contexts (sm/md) are
 * height-locked; roomy contexts (lg + `fullScreen`) fit the container
 * WIDTH up to a cap so the walk reads cinematic. The aspect ratio never
 * changes per surface - only the size does. See loader.module.css.
 *
 * Content placeholders (lists/grids that are populating, dashboard
 * widgets) keep using `WidgetSkeleton` - a skeleton communicates "content
 * is arriving" better than a spinner.
 */
import styles from './loader.module.css';

const WALK_SRC = '/brand/loader-walk.webp';
const WALK_STATIC_SRC = '/brand/loader-walk-static.webp';

export type LoaderSize = 'sm' | 'md' | 'lg';

export interface LoaderProps {
  /**
   * Size of the 16:9 walking mark. sm/md are height-locked (sm=20px
   * button/inline, md=52px); lg fits the container width up to 360px
   * (cards/panels). `fullScreen` overrides to a 520px width-fit.
   */
  size?: LoaderSize;
  /** Center the mark in a full-width, min-60vh column (route/page loads). */
  fullScreen?: boolean;
  /** Visible caption under the mark. Also used as the accessible name. */
  label?: string;
  /**
   * Accessible name when no visible `label` is shown (e.g. busy buttons).
   * Defaults to "Loading".
   */
  ariaLabel?: string;
  className?: string;
  'data-testid'?: string;
}

const sizeClass: Record<LoaderSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

export function Loader({
  size = 'md',
  fullScreen = false,
  label,
  ariaLabel,
  className,
  'data-testid': testId = 'loader',
}: LoaderProps) {
  const classes = [
    styles.root,
    sizeClass[size],
    fullScreen ? styles.fullScreen : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="status"
      aria-live="polite"
      aria-label={label ?? ariaLabel ?? 'Loading'}
      aria-busy={true}
      data-testid={testId}
    >
      <img
        className={styles.figure}
        src={WALK_SRC}
        alt=""
        aria-hidden="true"
        data-testid={`${testId}-figure`}
      />
      <img
        className={styles.figureStatic}
        src={WALK_STATIC_SRC}
        alt=""
        aria-hidden="true"
        data-testid={`${testId}-figure-static`}
      />
      {label ? <span className={styles.label}>{label}</span> : null}
    </div>
  );
}
